import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Paper, Grid } from '@material-ui/core';
import { formatEventStats, groupMetrics } from '../../utils/statsFormatter';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  rankItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  rankNumber: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '50%',
    marginRight: theme.spacing(2),
  },
  rankContent: {
    flex: 1,
  },
  rankBar: {
    height: '20px',
    borderRadius: '10px',
    marginTop: theme.spacing(1),
    transition: 'width 0.5s ease-in-out',
  },
  metricCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  noDataMessage: {
    padding: theme.spacing(4),
    textAlign: 'center',
  }
}));

const StackRankVisualization = ({ data, eventStats }) => {
  const classes = useStyles();
  
  // Enhanced debugging
  console.log('[StackRankVisualization] props:', { data, eventStats });
  console.log('[StackRankVisualization] rankings data:', data?.rankings);

  // Make sure we have valid rankings data
  const rankings = Array.isArray(data?.rankings) ? data.rankings : [];
  
  // Check if we have valid data to show
  if (rankings.length === 0) {
    console.warn('[StackRankVisualization] No ranking data available', data);
    return (
      <Paper className={classes.noDataMessage}>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Ranking Data Unavailable
        </Typography>
        <Typography variant="body2" color="textSecondary">
          There is insufficient data to display rankings. This may be because no responses have been recorded yet.
        </Typography>
      </Paper>
    );
  }

  // Sort by avgRank ascending (best rank = 1)
  const rankingsSorted = [...rankings].sort((a, b) => (a.avgRank ?? Infinity) - (b.avgRank ?? Infinity));

  // Create AI-generated summary based on available data
  const topOption = rankingsSorted[0]?.label || 'the top option';
  const bottomOption = rankingsSorted[rankingsSorted.length - 1]?.label || 'the bottom option';
  
  const aiSummary = rankings.length > 0
    ? `${topOption} is the preferred choice with an average rank of ${rankingsSorted[0]?.avgRank?.toFixed(2) ?? 'N/A'}. ${bottomOption} is the least preferred option.`
    : 'No ranking data is available to analyze preferences.';

  return (
    <Box className={classes.root}>
      {/* AI-Generated Summary Section */}
      <Paper className={classes.section} elevation={3} style={{ background: '#e3f2fd', borderLeft: '6px solid #1976d2' }}>
        <Typography variant="h5" gutterBottom style={{ fontWeight: 700, color: '#1976d2' }}>
          AI Key Takeaway
        </Typography>
        <Typography variant="body1">{aiSummary}</Typography>
      </Paper>

      {/* Rankings Bar Chart */}
      <Paper className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Rankings
        </Typography>
        {rankingsSorted.map((item, idx) => (
          <Box key={item.id || idx} className={classes.rankItem} style={{ boxShadow: idx === 0 ? '0 0 12px 2px #ffd70077' : undefined, border: idx === 0 ? '2px solid #FFD700' : undefined }}>
            <Box className={classes.rankNumber} style={{ backgroundColor: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : '#1976d2', color: idx < 3 ? '#222' : '#fff' }}>
              <Typography variant="h6">{idx + 1}</Typography>
            </Box>
            <Box className={classes.rankContent}>
              <Typography variant="subtitle1" style={{ fontWeight: idx === 0 ? 700 : 400 }}>{item.label}</Typography>
              <Box display="flex" alignItems="center" mb={0.5}>
                <Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
                  <span title="Average Rank">Avg: {item.avgRank != null ? item.avgRank.toFixed(2) : 'N/A'}</span>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <span title="Standard Deviation">Std Dev: {item.stdDev != null ? item.stdDev.toFixed(2) : 'N/A'}</span>
                </Typography>
              </Box>
              <Box 
                className={classes.rankBar} 
                style={{ 
                  width: `${100 - (item.avgRank ?? 0) * 20}%`, // Adjusted calculation for better visualization
                  minWidth: '10%', // Ensure there's always a visible bar
                  backgroundColor: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : (item.color || '#90caf9'),
                  boxShadow: idx === 0 ? '0 0 8px 2px #ffd70088' : undefined
                }} 
              />
            </Box>
          </Box>
        ))}
      </Paper>

      {/* Call to Action / Key Takeaway */}
      <Paper className={classes.section} elevation={2} style={{ background: '#f1f8e9', borderLeft: '6px solid #43a047' }}>
        <Typography variant="h6" style={{ color: '#388e3c', fontWeight: 700 }}>
          Next Step
        </Typography>
        <Typography variant="body1">
          Consider scheduling POPins on <b>{rankingsSorted[0]?.label ?? 'the top-ranked day'}</b> to maximize engagement. Review participant comments for further context.
        </Typography>
      </Paper>
    </Box>
  );
};

export default StackRankVisualization;